/**
 * shared/money.js
 *
 * The single arithmetic authority for money, imported by BOTH runtimes: Node
 * (server/**) and the browser (src/js/**, bundled by esbuild). Pure integer-pence
 * functions — NO imports, NO DOM, NO node builtins — so it runs unchanged in
 * either, and so scripts/test-settlement.mjs can exercise it in isolation.
 *
 * It lives here rather than under server/ because the compose screens have to
 * show the user the same figure the server will store, to the penny: instalment
 * validation compares them directly. That guarantee used to be a comment asking
 * two copies of this file to be kept "in lockstep" — src/js/lib/vat-bands.js was
 * byte-identical to the VAT banding below, and only the server copy was tested.
 * One module, imported twice, makes the drift impossible instead of policed.
 *
 * Everything is integer pence. The one tolerance below replaces the scattered
 * float 0.01 / 0.005 thresholds the codebase used to carry, so two payments of
 * 33.33 + 66.67 settle a 100.00 invoice cleanly instead of stranding it at a
 * fraction of a penny outstanding.
 */

// Settlement slack, in pence. An invoice is "fully paid" once cash + credit
// reaches within this of the total.
export const MONEY_TOLERANCE_PENCE = 1;

// The typed source of an invoice_payments row (migration 034). `synthetic` is
// the "mark as fully paid" top-up; `refund` rows carry a negative amount.
export const PAYMENT_METHODS = [
  "manual", // legacy typed-less recordings (pre-039)
  "stripe",
  "refund",
  "synthetic",
  // Typed manual methods (migration 039) — how clients actually pay
  // outside Stripe. A card taken on a card machine joined them in migration
  // 140. How a payment came in is read from `paid_by` (shared/paid-by.js),
  // which every row that knows carries; these stay as they are for what
  // already reads them (the CSV's Method column, the same-payment question).
  "bank_transfer",
  "cash",
  "card",
  "cheque",
  "other",
  // Stripe Pay by Bank / "Instant bank payment" (migration 056). Still a
  // Stripe settlement, but kept distinct from `stripe` (which means card):
  // the fees differ and the CSV export has to be able to tell them apart.
  "pay_by_bank"
];

// The Stripe-settled methods. Anything that looks up "the Stripe payment on
// this invoice" — refund matching above all — has to consider both, or a
// Pay-by-Bank payment silently becomes unrefundable.
export const STRIPE_PAYMENT_METHODS = ["stripe", "pay_by_bank"];

// Parse a NUMERIC(12,2) pounds value (string or number) into integer pence.
export function toPence(value) {
  const n = Number.parseFloat(String(value ?? "0"));
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

// Render integer pence back to a NUMERIC(12,2)-compatible pounds string.
export function fromPence(pence) {
  return (Number(pence) / 100).toFixed(2);
}

// ── What a line comes to (ADR-034) ───────────────────────────────────────────
//
// A line on any document is its quantity times its unit price, rounded to the
// penny ON ITS OWN, half up, and a document's subtotal is the sum of those
// amounts. So the lines a document prints add up to the subtotal it prints,
// and Labour and Materials (#961) to the subtotal they stand in for, whatever
// the quantities are.
//
// Half up because it is what somebody checking the document with a calculator
// does, what the document has always printed (Intl.NumberFormat rounds half
// up), and what computeVatBands below does with a band's VAT. Worked in whole
// numbers and never in floating point: 1.5 × £1.03 is exactly 154.5p, which is
// £1.55, where `1.5 * 1.03` is 1.5449999… in binary and rounds down.
//
// It used to be three rules: the subtotal was the floating-point sum of the
// UNROUNDED lines, the stored `line_total` was each line's `toFixed(2)`
// (binary, so about half the half-pennies went down), and the VAT bands and
// the document rounded each line with `Math.round` of a float product. With
// whole-penny lines, which is nearly every document, all three agreed.

// A value as decimal text: what was typed or sent. A number is read by its
// shortest form (String(n)), which is the decimal somebody typed and the text
// node-postgres writes into a NUMERIC column. Exponent forms (1e-7) are
// written out. "" for anything that isn't a finite number.
function decimalText(value) {
  if (value == null) return "";
  let text = typeof value === "number" ? (Number.isFinite(value) ? String(value) : "") : String(value).trim();
  if (/e/i.test(text)) {
    const n = Number(text);
    text = Number.isFinite(n) && Math.abs(n) < 1e21 ? n.toFixed(20) : "";
  }
  return /^[+-]?(\d+\.?\d*|\.\d+)$/.test(text) ? text : "";
}

/**
 * A decimal value in hundredths, rounded half away from zero from the decimal
 * as written, which is how a NUMERIC(…, 2) column rounds it: "1.555" and 1.555
 * are 156, 1.545 is 155, "2" is 200. NaN for anything that isn't a number.
 */
export function toHundredths(value) {
  const text = decimalText(value);
  if (!text) return Number.NaN;
  const negative = text.startsWith("-");
  const [whole, fraction = ""] = text.replace(/^[+-]/, "").split(".");
  const digits = fraction.padEnd(3, "0");
  const hundredths = Number(whole || "0") * 100 + Number(digits.slice(0, 2)) + (Number(digits[2]) >= 5 ? 1 : 0);
  return negative ? -hundredths : hundredths;
}

/**
 * A unit price in pounds, as its row keeps it, in pence: to the penny, half up
 * from the decimal as written (toHundredths), so a price typed as £10.005 is
 * the £10.01 its NUMERIC(12,2) column stores. 0 for anything that isn't a
 * number, as toPence.
 */
export function unitPriceToPence(value) {
  const pence = toHundredths(value);
  return Number.isSafeInteger(pence) ? pence : 0;
}

/**
 * Whether a value is written to no more than two decimal places, so that its
 * row keeps it exactly: "1.5", "1.50" and "1.500" are, 1.555 isn't. False for
 * anything that isn't a number.
 */
export function fitsHundredths(value) {
  const text = decimalText(value);
  if (!text) return false;
  const fraction = text.split(".")[1] || "";
  return /^\d{0,2}0*$/.test(fraction);
}

// A quantity in hundredths times a price in pence is in hundredths of a
// penny: this is that in pence, rounded half away from zero, exactly. In
// Numbers while they are exact, else BigInt.
function hundredthsToPence(product) {
  if (Number.isSafeInteger(product)) {
    const size = Math.abs(product);
    const rest = size % 100;
    const pence = (size - rest) / 100 + (rest >= 50 ? 1 : 0);
    return product < 0 ? -pence : pence;
  }
  const size = product < BigInt(0) ? -product : product;
  const rest = size % BigInt(100);
  const pence = Number(size / BigInt(100) + (rest >= BigInt(50) ? BigInt(1) : BigInt(0)));
  return product < BigInt(0) ? -pence : pence;
}

/**
 * What a line comes to, in pence: its quantity, at the places a line keeps
 * (`toHundredths`), times its unit price in whole pence, rounded half up to
 * the penny. THE one rule for a line's amount: what computeDocument stores
 * and adds up, what the VAT bands, the labour and the materials are built
 * from, what the document, the e-invoice, the pay page and both compose
 * screens print. 0 when either isn't a number.
 */
export function lineAmountPence(quantity, unitPricePence) {
  const hundredths = toHundredths(quantity);
  const price = Math.round(Number(unitPricePence));
  if (!Number.isSafeInteger(hundredths) || !Number.isSafeInteger(price)) return 0;
  const product = hundredths * price;
  // Past 2^53 (a line of more than £900 billion) the product is worked in
  // BigInt, so it stays exact rather than nearly right.
  return hundredthsToPence(Number.isSafeInteger(product) ? product : BigInt(hundredths) * BigInt(price));
}

/**
 * What a line came to under the rule BEFORE ADR-034, in pence, from a price
 * in pounds: the floating-point product rounded, `Math.round(q × p × 100)`.
 * FOR READING A DOCUMENT STORED BEFORE THAT RULE, AND NOTHING ELSE: it is
 * what that document's VAT bands, its labour and materials and its e-invoice
 * were worked out from, so it is how such a document is read when the rule
 * no longer gives back the figures it was stored with (`documentLinePence`
 * in shared/document-markup.js, `invoiceDocumentRate` in
 * shared/credit-note.js). It differs from `lineAmountPence` only on a line
 * costing exactly half a penny that binary rounded down (1.5 × £1,795.35 is
 * 269302.5p, which this gives as 269302). Never compose with it.
 */
export function preRuleLinePence(quantity, unitPricePounds) {
  return Math.max(0, Math.round((Number(quantity) || 0) * (Number(unitPricePounds) || 0) * 100));
}

// Outstanding balance in pence: total − cash paid − credit applied − CIS
// deduction, floored at 0. Accepts a raw invoice row (snake_case NUMERIC
// columns). The CIS deduction (migration 042) is money the contractor pays
// to HMRC on the subcontractor's behalf, so it settles the invoice without
// ever arriving as cash — same treatment as a credit note. A caller whose
// SELECT omits cis_deduction_amount fails safe: outstanding reads too high,
// never too low.
//
// What stands of the deduction is the invoice's own less what credit notes
// took off it (`cisDeductionNowFromRow`): a caller whose SELECT omits
// cis_credited_pence reads the whole deduction, and so an outstanding too low
// once labour has been credited. Select both.
export function computeOutstandingPence(invoiceRow) {
  return outstandingPenceOf({
    totalPence: toPence(invoiceRow.total_amount),
    paidPence: toPence(invoiceRow.amount_paid),
    creditedPence: toPence(invoiceRow.amount_credited),
    cisDeductionPence: cisDeductionNowFromRow(invoiceRow)
  });
}

// ---------------------------------------------------------------------------
// CIS on labour that was credited (ADR-031, amended 30 October 2026).
//
// A contractor deducts CIS only from the labour they actually pay for (CIS
// 340, 3.12), so a credit note that credits labour takes that labour's CIS
// off the invoice's deduction: credit an £1,800.00 invoice's £1,200.00 labour
// line and the £200.00 deducted from it goes too, leaving £600.00 owed, not
// £400.00. The invoice's own deduction (`cis_deduction_amount`) never moves:
// it is what the invoice said. What the credit notes sent against it took off
// is kept beside it (`cis_credited_pence`, the sum over them, recomputed as
// `amount_credited` is), and what still stands is the one less the other.
// Every reader of a balance, a share or a deduction to come reads this.
// ---------------------------------------------------------------------------

/** What stands of an invoice's CIS deduction once credit notes took some off. */
export function cisDeductionNowPence({ cisDeductionPence = 0, cisCreditedPence = 0 } = {}) {
  const deduction = Math.max(0, Math.round(Number(cisDeductionPence) || 0));
  const credited = Math.max(0, Math.round(Number(cisCreditedPence) || 0));
  return Math.max(0, deduction - credited);
}

/** The same, from an invoice row as the database has it. */
export function cisDeductionNowFromRow(invoiceRow) {
  return cisDeductionNowPence({
    cisDeductionPence: toPence(invoiceRow?.cis_deduction_amount),
    cisCreditedPence: Number(invoiceRow?.cis_credited_pence ?? 0)
  });
}

/** The same, from an invoice as the API serves it (`…Pence`). */
export function cisDeductionNowOf(invoice) {
  return cisDeductionNowPence({
    cisDeductionPence: invoice?.cisDeductionAmountPence,
    cisCreditedPence: invoice?.cisCreditedPence
  });
}

/**
 * The shares of a deduction a deposit or a plan's stages carry, once credit
 * notes took `cisCreditedPence` off it: what still stands, split in
 * proportion to the shares as stored (themselves in proportion to the
 * payments, splitCisAcrossPayments), so they still add up to it exactly and
 * every stage's target rises by its part. Unchanged with nothing credited.
 */
export function cisSharesNow(sharesPence, cisCreditedPence = 0) {
  const shares = (Array.isArray(sharesPence) ? sharesPence : []).map((s) => Math.max(0, Math.round(Number(s) || 0)));
  const credited = Math.max(0, Math.round(Number(cisCreditedPence) || 0));
  if (credited === 0) return shares;
  const deduction = shares.reduce((sum, s) => sum + s, 0);
  return splitCisAcrossPayments(shares, Math.max(0, deduction - credited));
}

/**
 * A plan's stages with their CIS shares as they stand once credit notes took
 * `cisCreditedPence` off the deduction (`cisSharesNow`). Reads and returns
 * rows as the database or the API has them, keeping whichever spelling each
 * share was in. The same rows with nothing credited.
 */
export function instalmentsWithCisNow(instalments, cisCreditedPence = 0) {
  const rows = Array.isArray(instalments) ? instalments : [];
  if (!(Math.round(Number(cisCreditedPence) || 0) > 0) || rows.length === 0) return rows;
  const shareOf = (row) => Number(row?.cis_deduction_pence ?? row?.cisDeductionPence ?? 0) || 0;
  const shares = cisSharesNow(rows.map(shareOf), cisCreditedPence);
  return rows.map((row, i) => {
    const camel = row && Object.prototype.hasOwnProperty.call(row, "cisDeductionPence");
    const snake = !camel || Object.prototype.hasOwnProperty.call(row, "cis_deduction_pence");
    return { ...row, ...(snake ? { cis_deduction_pence: shares[i] } : {}), ...(camel ? { cisDeductionPence: shares[i] } : {}) };
  });
}

/**
 * A deposit's share of the deduction once credit notes took some off it: the
 * deposit's and the balance's shares as stored, split again over what stands.
 */
export function depositCisNowPence({ depositCisDeductionPence = 0, cisDeductionPence = 0, cisCreditedPence = 0 } = {}) {
  const deposit = Math.max(0, Math.round(Number(depositCisDeductionPence) || 0));
  const deduction = Math.max(0, Math.round(Number(cisDeductionPence) || 0));
  return cisSharesNow([deposit, Math.max(0, deduction - deposit)], cisCreditedPence)[0] ?? 0;
}

/**
 * The same sum from figures already in pence, which is how the API sends them
 * (`…Pence` on every serialized document). The web reads these rather than the
 * pounds twins, so no balance is worked out from a float.
 */
export function outstandingPenceOf({ totalPence, paidPence, creditedPence, cisDeductionPence, cisCreditedPence = 0 } = {}) {
  const n = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
  const cis = cisDeductionNowPence({ cisDeductionPence: n(cisDeductionPence), cisCreditedPence: n(cisCreditedPence) });
  return Math.max(0, n(totalPence) - n(paidPence) - n(creditedPence) - cis);
}

// What has settled an invoice so far, kept apart, for a page the client reads.
// Cash, credit notes and a CIS deduction all reduce the balance (above), but
// only cash is money the client paid: a contractor withholds the CIS for HMRC,
// and a credit note is the sender taking something off. The public pay page
// once showed total − outstanding as "Paid", so a contractor who had paid
// nothing on a CIS invoice read that they'd paid the deduction. Accepts a raw
// invoice row, like computeOutstandingPence; each part floors at 0.
export function settlementBreakdown(invoiceRow) {
  return {
    paidPence: Math.max(0, toPence(invoiceRow.amount_paid)),
    creditedPence: Math.max(0, toPence(invoiceRow.amount_credited)),
    // What stands of it, once credit notes took the CIS on credited labour off.
    cisDeductionPence: cisDeductionNowFromRow(invoiceRow),
    outstandingPence: computeOutstandingPence(invoiceRow)
  };
}

// CIS deduction in pence: rate % of the labour element (excl. VAT), clamped
// to [0, labour]. The invoice total is untouched — the deduction only reduces
// what the contractor pays in cash.
export function computeCisDeductionPence({ rate, labourPence }) {
  const r = Number.parseFloat(String(rate ?? ""));
  if (!Number.isFinite(r) || r <= 0 || labourPence <= 0) return 0;
  return Math.max(0, Math.min(Math.round((labourPence * r) / 100), labourPence));
}

/**
 * Derive the post-payment settlement state.
 *
 * `deltaPence` is the cash change: positive for a payment, negative for a
 * refund/reversal. `newPaidPence` is never clamped down — money that actually
 * moved is recorded even if it pushes past the total (`overpaidPence` surfaces
 * the excess); readers use `outstandingPence`, which floors at 0.
 *
 * Status keys on CASH for the partial-vs-sent distinction (a credit note is not
 * a payment, so a purely credited-but-unpaid invoice stays `sent`), while full
 * settlement counts cash + credit (so a credit note can complete an invoice).
 */
/**
 * Freeze a deposit into integer pence against the invoice total.
 *
 * `percent` → round(total × value/100); `fixed` → round(value × 100). The result
 * is clamped to [0, total] so a deposit can never exceed the invoice. The balance
 * is the total minus the deposit **by subtraction** — the two always reconcile to
 * the penny (never round both independently). The percentage is display metadata;
 * this pence amount is the single value money math reads. Returns null when there
 * is no deposit.
 */
/**
 * Compute the discount in pence from its type/value pair (migration 041).
 * Applied to the SUBTOTAL before VAT: net = subtotal − discount, tax is
 * charged on the net. Clamped to [0, subtotal] so a discount can never turn
 * an invoice negative. Returns 0 when there is no discount — callers can
 * always subtract it unconditionally.
 */
export function computeDiscountPence({ discountType, discountValue, subtotalPence }) {
  if (discountType !== "percent" && discountType !== "fixed") return 0;
  // At the places its column keeps (NUMERIC(12,2)): a percentage to two, a
  // fixed amount to the penny, each half up from the decimal as written, so
  // a draft saved at 12.345% is worked out at the 12.35% it is stored at, and
  // reopening it works out the same discount (ADR-034).
  const hundredths = termHundredths(discountValue);
  if (!(hundredths > 0)) return 0;
  const raw = discountType === "percent"
    ? Math.round((subtotalPence * (hundredths / 100)) / 100)
    : hundredths;
  return Math.max(0, Math.min(raw, subtotalPence));
}

// A discount's or a deposit's value in hundredths (of a percent, or of a
// pound: pence), as its NUMERIC(12,2) column keeps it. NaN for anything that
// isn't a number, as parseFloat read it: "12%" is 12.
function termHundredths(value) {
  const parsed = Number.parseFloat(String(value ?? ""));
  if (!Number.isFinite(parsed)) return Number.NaN;
  const text = String(value ?? "").trim();
  const hundredths = toHundredths(/^[+-]?(\d+\.?\d*|\.\d+)$/.test(text) ? text : parsed);
  return Number.isSafeInteger(hundredths) ? hundredths : Number.NaN;
}

/**
 * Group line items into VAT rate bands and compute the tax (migration 045).
 *
 * Each line's rate is its own `vatRate` when set, else `documentRate` — so an
 * invoice with no per-line rates collapses to one band at the document rate
 * and reproduces the pre-045 maths to the penny. A document discount is
 * allocated across lines proportionally by value BEFORE banding (largest-
 * remainder penny reconciliation, ties broken by line order), so the banded
 * nets always sum exactly to subtotal − discount. Band tax = round(net ×
 * rate/100) per band; `taxPence` is the band sum.
 *
 * Returns { bands: [{ rate, netPence, taxPence }] (rate-ascending),
 * taxPence }. Callers own the gating: pass documentRate 0 (and null line
 * rates) for non-GBP documents, and ignore taxPence under reverse charge
 * (it is the "would-be" figure for the document note).
 */
export function computeVatBands({ lines, documentRate, discountPence = 0, retentionPence = 0 }) {
  const docRate = Number.parseFloat(String(documentRate ?? ""));
  const safeDocRate = Number.isFinite(docRate) && docRate > 0 ? docRate : 0;
  const parsed = (Array.isArray(lines) ? lines : [])
    .map((line) => {
      const linePence = Math.max(0, Math.round(Number(line?.linePence) || 0));
      const raw = line?.vatRate;
      const own = raw == null || String(raw).trim() === "" ? null : Number.parseFloat(String(raw));
      const rate = own != null && Number.isFinite(own) && own >= 0 ? own : safeDocRate;
      return { linePence, rate };
    });

  // Proportional discount allocation, penny-reconciled (largest remainder).
  const shares = lineDiscountSharesPence(parsed.map((l) => l.linePence), discountPence);
  const discounted = parsed.map((l, i) => ({ ...l, netPence: l.linePence - shares[i] }));

  const byRate = new Map();
  for (const l of discounted) {
    byRate.set(l.rate, (byRate.get(l.rate) || 0) + l.netPence);
  }
  const sorted = [...byRate.entries()].sort((a, b) => a[0] - b[0]);

  // A RETENTION (#932) comes off after the discount and before VAT, spread
  // over the bands by what each is worth, as the discount is spread over the
  // lines. VAT on a retention is due when it is invoiced or paid, whichever is
  // first (VAT Regulations 1995, reg. 89), so the tax here is charged on each
  // band less its share, and the share is kept on the band for the release
  // invoice, which charges it at the same rate. With no retention every band
  // is exactly what it always was, with no extra field.
  const netTotal = sorted.reduce((sum, [, net]) => sum + net, 0);
  const retention = Math.max(0, Math.min(Math.round(Number(retentionPence) || 0), netTotal));
  if (retention > 0) {
    const shares = allocatePence(retention, sorted.map(([, net]) => net));
    const bands = sorted.map(([rate, gross], i) => {
      const netPence = gross - shares[i];
      return { rate, netPence, taxPence: Math.round((netPence * rate) / 100), retentionPence: shares[i] };
    });
    return { bands, taxPence: bands.reduce((sum, b) => sum + b.taxPence, 0) };
  }

  const bands = sorted.map(([rate, netPence]) => ({ rate, netPence, taxPence: Math.round((netPence * rate) / 100) }));
  return { bands, taxPence: bands.reduce((sum, b) => sum + b.taxPence, 0) };
}

/**
 * Each line's share of a document discount, in whole pence, in line order:
 * in proportion to the line's value, rounded down, then the pennies left one
 * each to the largest remainders, ties to the earlier line, so the shares add
 * up to the discount exactly. The ONE way a discount is spread over lines:
 * the VAT bands are built from it (computeVatBands), and so is the labour a
 * CIS deduction is taken on when a document's lines say which are labour
 * (shared/compute-document.js), so the two never put a penny of discount on
 * different lines. The discount is clamped to [0, the lines' total]; all
 * zeros when there is none.
 */
export function lineDiscountSharesPence(linePences, discountPence = 0) {
  const lines = (Array.isArray(linePences) ? linePences : []).map((p) => Math.max(0, Math.round(Number(p) || 0)));
  const subtotalPence = lines.reduce((sum, p) => sum + p, 0);
  const discount = Math.max(0, Math.min(Math.round(Number(discountPence) || 0), subtotalPence));
  if (discount <= 0 || subtotalPence <= 0) return lines.map(() => 0);
  const shares = lines.map((p) => (p * discount) / subtotalPence);
  const floors = shares.map((s) => Math.floor(s));
  let remainder = discount - floors.reduce((sum, f) => sum + f, 0);
  const order = shares
    .map((s, i) => ({ i, frac: s - Math.floor(s) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  const extra = lines.map(() => 0);
  for (const { i } of order) {
    if (remainder <= 0) break;
    extra[i] = 1;
    remainder -= 1;
  }
  return lines.map((_, i) => floors[i] + extra[i]);
}

/**
 * Share `totalPence` out over `weights` in proportion, in whole pence that add
 * up to it exactly: each share rounded down, then the pennies left over one
 * each to the largest remainders, ties to the earlier weight. The rule a
 * discount is spread over lines by (above), written once for anything else
 * spread the same way: a retention over VAT bands and over its parts.
 * All zeros when there is nothing to share or nothing to share it by.
 */
export function allocatePence(totalPence, weights) {
  const ws = (Array.isArray(weights) ? weights : []).map((w) => Math.max(0, Math.round(Number(w) || 0)));
  const sum = ws.reduce((a, b) => a + b, 0);
  const total = Math.max(0, Math.round(Number(totalPence) || 0));
  if (ws.length === 0) return [];
  if (total === 0 || sum === 0) return ws.map(() => 0);
  const shares = ws.map((w) => Math.floor((total * w) / sum));
  let left = total - shares.reduce((a, b) => a + b, 0);
  const order = ws
    .map((w, i) => ({ i, remainder: (total * w) % sum }))
    .sort((a, b) => b.remainder - a.remainder || a.i - b.i);
  for (const { i } of order) {
    if (left <= 0) break;
    shares[i] += 1;
    left -= 1;
  }
  return shares;
}

/**
 * A retention in pence: `rate` per cent of the value of the work after any
 * discount, before VAT, to the nearest penny, never more than that value.
 * 0 for no rate, a rate of 0 or less, or nothing to hold it from.
 */
export function computeRetentionPence({ rate, netPence }) {
  const r = Number.parseFloat(String(rate ?? ""));
  const net = Math.max(0, Math.round(Number(netPence) || 0));
  if (!Number.isFinite(r) || r <= 0 || net <= 0) return 0;
  return Math.max(0, Math.min(Math.round((net * Math.min(r, 100)) / 100), net));
}

/**
 * How a retention comes back, as its parts (#932): two halves (the first
 * rounded down, the second the rest, so they always add up, as a deposit and
 * its balance do) or one payment. Each part carries its share of the labour
 * held, for the CIS deduction on its release, and of each VAT band, so its
 * release invoice charges VAT at the rate it was held at (VAT Notice 708,
 * 2.1.1). A part worth nothing is left out.
 *
 * `bands` are computeVatBands' bands, which carry `retentionPence` when there
 * is a retention. `labourPence` is the labour's share of the whole retention.
 */
export function splitRetention({ retentionPence = 0, release = "halves", bands = [], labourPence = 0 } = {}) {
  const total = Math.max(0, Math.round(Number(retentionPence) || 0));
  if (total === 0) return [];
  const labour = Math.max(0, Math.min(total, Math.round(Number(labourPence) || 0)));
  const halves = release !== "single";
  const amounts = halves ? [Math.floor(total / 2), total - Math.floor(total / 2)] : [total];
  const labours = halves ? [Math.floor(labour / 2), labour - Math.floor(labour / 2)] : [labour];
  const bandList = (Array.isArray(bands) ? bands : []).filter((b) => (Number(b?.retentionPence) || 0) > 0);
  let remaining = bandList.map((b) => Number(b.retentionPence) || 0);
  const parts = amounts.map((amountPence, i) => {
    const last = i === amounts.length - 1;
    const shares = last ? remaining : allocatePence(amountPence, remaining);
    remaining = remaining.map((r, j) => r - shares[j]);
    return {
      seq: i + 1,
      amountPence,
      labourPence: labours[i],
      vatBands: bandList.map((b, j) => ({ rate: b.rate, pence: shares[j] })).filter((b) => b.pence > 0)
    };
  });
  return parts.filter((part) => part.amountPence > 0);
}

export function computeDeposit({ depositType, depositValue, totalPence }) {
  if (depositType !== "percent" && depositType !== "fixed") return null;
  // At the places its column keeps, as a discount is (computeDiscountPence).
  const hundredths = termHundredths(depositValue);
  if (!(hundredths > 0)) return null;
  const raw = depositType === "percent"
    ? Math.round((totalPence * (hundredths / 100)) / 100)
    : hundredths;
  const depositPence = Math.max(0, Math.min(raw, totalPence));
  return { depositPence, balancePence: totalPence - depositPence };
}

export function computeSettlement({ totalPence, currentPaidPence, creditedPence = 0, cisDeductionPence = 0, deltaPence = 0 }) {
  // CIS sits on the non-cash side with credit: it completes settlement but
  // never flips an invoice to partially_paid on its own.
  const nonCashPence = creditedPence + cisDeductionPence;
  const newPaidPence = Math.max(0, currentPaidPence + deltaPence);
  const fullyPaid = newPaidPence + nonCashPence >= totalPence - MONEY_TOLERANCE_PENCE;
  const outstandingPence = Math.max(0, totalPence - newPaidPence - nonCashPence);
  const overpaidPence = Math.max(0, newPaidPence + nonCashPence - totalPence);

  let newStatus;
  if (fullyPaid) newStatus = "paid";
  else if (newPaidPence > MONEY_TOLERANCE_PENCE) newStatus = "partially_paid";
  else newStatus = "sent";

  return { newPaidPence, outstandingPence, overpaidPence, fullyPaid, newStatus };
}

// ---------------------------------------------------------------------------
// A CIS deduction spread over the payments an invoice is paid in.
//
// HMRC takes CIS at each payment, advances included (CIS 340 §2.16), so on an
// invoice with a deposit or a payment plan each payment carries its own part of
// the deduction. HMRC sets no rule for how much: the contractor works out the
// materials in each payment and may estimate them (CISR15060). So the invoice
// states an expectation, and the neutral one is in proportion to each
// payment's size — it says nothing about where the materials fall, and a
// contractor who deducts more on one stage and less on another still lands on
// the same total.
//
// Each share is rounded down, and the pennies that leaves go one each to the
// payments with the largest remainders — the last of equals first — so the
// shares add up to the deduction exactly and none is ever more than its own
// payment. (Giving every leftover penny to the last payment could: a 1p final
// stage could be handed 3p.) All zeros when there is no deduction, which is
// what every invoice without CIS stores.
export function splitCisAcrossPayments(amountsPence, cisDeductionPence) {
  const amounts = (Array.isArray(amountsPence) ? amountsPence : []).map((a) => Math.max(0, Math.round(Number(a) || 0)));
  const total = amounts.reduce((sum, a) => sum + a, 0);
  // Never more than the payments themselves: a deduction is taken from them.
  const deduction = Math.min(total, Math.max(0, Math.round(Number(cisDeductionPence) || 0)));
  if (amounts.length === 0) return [];
  if (deduction === 0 || total === 0) return amounts.map(() => 0);
  const shares = amounts.map((a) => Math.floor((deduction * a) / total));
  let left = deduction - shares.reduce((sum, s) => sum + s, 0);
  const byRemainder = amounts
    .map((a, i) => ({ i, remainder: (deduction * a) % total }))
    .sort((x, y) => y.remainder - x.remainder || y.i - x.i);
  for (const { i } of byRemainder) {
    if (left === 0) break;
    shares[i] += 1;
    left -= 1;
  }
  return shares;
}

// The deposit's part of a CIS deduction, when an invoice is paid as a deposit
// and a balance. One call, so the compose screen, the send that freezes it and
// the document all ask the same thing.
export function depositCisSharePence({ depositPence = 0, balancePence = 0, cisDeductionPence = 0 } = {}) {
  return splitCisAcrossPayments([depositPence, balancePence], cisDeductionPence)[0] ?? 0;
}

// What a stage of a plan is met by: its amount less its share of the CIS
// deduction, which the contractor pays to HMRC rather than to the sender. Only
// cash and credit count towards it. Reads a row as the database or the API has
// it; a share that isn't there is 0, as on every invoice without CIS.
export function instalmentTargetPence(row) {
  const amount = Number(row?.amount_pence ?? row?.amountPence ?? 0) || 0;
  const share = Number(row?.cis_deduction_pence ?? row?.cisDeductionPence ?? 0) || 0;
  return Math.max(0, amount - share);
}

// What a deposit is met by: the deposit less its share of the CIS deduction.
// Met by CASH only — a credit note does not pay a deposit.
export function depositTargetPence({ depositAmountPence = 0, depositCisDeductionPence = 0 } = {}) {
  return Math.max(0, (Number(depositAmountPence) || 0) - (Number(depositCisDeductionPence) || 0));
}

// Whether a deposit has been met, from an invoice row (snake_case) — null when
// the invoice has no frozen deposit. The one test every reader of "deposit met"
// goes through.
export function depositMetFromRow(row) {
  if (row?.deposit_amount_pence == null) return null;
  return toPence(row.amount_paid) >= depositTargetPence({
    depositAmountPence: Number(row.deposit_amount_pence),
    depositCisDeductionPence: depositCisNowPence({
      depositCisDeductionPence: Number(row.deposit_cis_deduction_pence ?? 0),
      cisDeductionPence: toPence(row.cis_deduction_amount),
      cisCreditedPence: Number(row.cis_credited_pence ?? 0)
    })
  });
}

// What has been paid towards an invoice's payments — its deposit, or its
// plan's stages — in pence: cash and credit, never the CIS deduction, which is
// already taken out of each payment's target. Reads an invoice row as the
// database has it. The one figure every caller of allocateInstalments and
// nextAmountDue passes, so none of them decides afresh whether CIS counts.
export function settledTowardsPaymentsPence(row) {
  return toPence(row?.amount_paid) + toPence(row?.amount_credited);
}

// Allocate settled money (cash + credit, in pence — never the CIS deduction)
// across ordered instalments, oldest-first: an instalment is "paid" once the
// running total covers its target (its amount less its CIS share). Pure.
// Returns the set of paid instalment ids and the seq of the first unpaid one.
//
// `cisCreditedPence` is what credit notes took off the invoice's deduction:
// each stage is then met at its amount less its share of what still stands
// (instalmentsWithCisNow). Pass the invoice's `cis_credited_pence`.
export function allocateInstalments(instalments, settledPence, { cisCreditedPence = 0 } = {}) {
  const ordered = [...instalmentsWithCisNow(instalments, cisCreditedPence)].sort((a, b) => a.seq - b.seq);
  const paidIds = new Set();
  let running = 0;
  let nextDueSeq = null;
  for (const ins of ordered) {
    running += instalmentTargetPence(ins);
    if (running <= settledPence + MONEY_TOLERANCE_PENCE) {
      paidIds.add(ins.id);
    } else if (nextDueSeq === null) {
      nextDueSeq = ins.seq;
    }
  }
  return { paidIds, nextDueSeq };
}

// ---------------------------------------------------------------------------
// What an invoice is waiting on, and when it was due.
//
// An invoice has ONE date in the database, but it can be waiting on something
// else entirely. Three mutually exclusive phases, in precedence order:
//
//   instalment  a milestone plan chases its next unpaid instalment, anchored to
//               that instalment's own due date, re-anchoring as each is paid
//   deposit     an unpaid deposit with its own due date is chased first
//   balance     everything else is the invoice's own due date
//
// This lived inside server/reminders.js, where the reminder engine had it right
// while everything that labelled an invoice "overdue" read due_date alone. So a
// six-month plan on a 30-day invoice was chased correctly per instalment and
// still turned red in the list after 30 days, with nothing late. One function,
// so the chaser and the label cannot disagree again.
//
// Returns null when there is nothing left to chase — every instalment covered.
// Deposits and plans are mutually exclusive on an invoice, so the instalment
// branch takes precedence cleanly. `settledPence` is cash + credit; a CIS
// deduction is already taken out of each instalment's and the deposit's target.
export function nextAmountDue({
  dueDate = null,
  instalments = [],
  settledPence = 0,
  paidPence = 0,
  depositAmountPence = 0,
  depositCisDeductionPence = 0,
  depositDueDate = null,
  // The invoice's deduction and what credit notes took off it: a deposit's
  // and a stage's share is of what still stands (ADR-031, amended).
  cisDeductionPence = 0,
  cisCreditedPence = 0
} = {}) {
  if (Array.isArray(instalments) && instalments.length > 0) {
    const { nextDueSeq } = allocateInstalments(instalments, settledPence, { cisCreditedPence });
    if (nextDueSeq == null) return null;
    const target = [...instalments].find((ins) => ins.seq === nextDueSeq);
    return { phase: "instalment", dueDate: target?.due_date ?? null, instalmentSeq: nextDueSeq };
  }
  const depositPence = Number(depositAmountPence) || 0;
  const depositShare = depositCisNowPence({ depositCisDeductionPence, cisDeductionPence, cisCreditedPence });
  if (depositPence > 0 && Number(paidPence) < depositTargetPence({ depositAmountPence, depositCisDeductionPence: depositShare }) && depositDueDate) {
    return { phase: "deposit", dueDate: depositDueDate, instalmentSeq: null };
  }
  return { phase: "balance", dueDate, instalmentSeq: null };
}

// ---------------------------------------------------------------------------
// Refundable cash on an invoice.
//
// "Refundable" is narrower than "paid": only money Stripe actually collected
// can be sent back through Stripe. Cash, cheque and bank-transfer payments are
// recorded here too, but refunding those happens outside Sendinvo — that's
// what the separate "Record refund" control is for.
//
// Both Stripe-settled methods count: a Pay by Bank payment refunds to the
// payer's bank account for up to two years after it settles.
//
// The single definition of this rule. It backs both the POST that performs a
// refund and the GET the drawer uses to decide whether to offer one, so the
// button can't appear for an amount the endpoint would then reject.
// ---------------------------------------------------------------------------

export const REFUNDABLE_METHODS = ["stripe", "pay_by_bank"];

export function computeRefundable(paymentRows) {
  const rows = paymentRows || [];
  const pence = (row) => Math.round(Number.parseFloat(String(row.amount || 0)) * 100);
  let collectedPence = 0;
  let refundedPence = 0;
  // Each payment Stripe took, by its PaymentIntent, in the order the rows
  // came (oldest first): a refund goes against one PaymentIntent, and Stripe
  // refuses one for more than that payment has left.
  const byIntent = new Map();
  for (const row of rows) {
    if (!REFUNDABLE_METHODS.includes(row.method)) continue;
    collectedPence += pence(row);
    const id = row.stripe_payment_intent_id;
    if (!id) continue;
    const entry = byIntent.get(id) || { paymentIntentId: id, collectedPence: 0, refundedPence: 0 };
    entry.collectedPence += pence(row);
    byIntent.delete(id);
    byIntent.set(id, entry);
  }
  for (const row of rows) {
    if (row.method !== "refund") continue;
    // Refunds are stored negative; count them as positive already-returned.
    const back = Math.abs(pence(row));
    refundedPence += back;
    // A refund through Stripe names its PaymentIntent; one recorded by hand
    // doesn't, and comes off the total below.
    const entry = row.stripe_payment_intent_id ? byIntent.get(row.stripe_payment_intent_id) : null;
    if (entry) entry.refundedPence += back;
  }
  // Newest first, each with what it has left.
  const intents = [...byIntent.values()].reverse().map((entry) => ({
    ...entry,
    refundablePence: Math.max(0, entry.collectedPence - entry.refundedPence)
  }));
  const onIntentsPence = intents.reduce((sum, entry) => sum + entry.refundablePence, 0);
  // Never negative: a refund recorded by hand against a Stripe payment could
  // otherwise push this below zero and read as "owed back to you". And never
  // more than the payments have left between them, which is what Stripe
  // can refund.
  const refundablePence = Math.max(0, Math.min(collectedPence - refundedPence, onIntentsPence));
  const paymentIntentId = (intents.find((entry) => entry.refundablePence > 0) || intents[0] || {}).paymentIntentId ?? null;
  return { refundablePence, paymentIntentId, collectedPence, refundedPence, intents };
}

/**
 * A refund of `amountPence` split across the payments it can go against,
 * newest first, each for no more than that payment has left: one Stripe
 * refund each. `intents` is computeRefundable's. Empty when the amount is
 * more than they can take between them.
 */
export function planStripeRefund(intents, amountPence) {
  const plan = [];
  let left = Math.max(0, Math.round(Number(amountPence) || 0));
  for (const entry of intents || []) {
    if (left <= 0) break;
    const take = Math.min(left, entry.refundablePence);
    if (take <= 0) continue;
    plan.push({ paymentIntentId: entry.paymentIntentId, amountPence: take, refundedPence: entry.refundedPence });
    left -= take;
  }
  return left > 0 ? [] : plan;
}

// The rates an invoice's lines carry of their own, as numbers.
function ownLineRates(lineRates = []) {
  return (Array.isArray(lineRates) ? lineRates : [])
    .map((r) => Number.parseFloat(String(r ?? "")))
    .filter((r) => Number.isFinite(r));
}

/**
 * Whether the rate a document is served at is the one it stored (migration
 * 151) rather than one read off its lines or worked back out of its totals:
 * it stored one, it isn't under the reverse charge, and none of its lines
 * carries a rate of its own (`documentVatRateFromTotals`). A credit note
 * against it takes that rate as it is (shared/credit-note.js), where one
 * worked back out is put right first.
 */
export function servesStoredVatRate({ reverseCharge = false, lineRates = [], storedRate = null } = {}) {
  if (reverseCharge || ownLineRates(lineRates).length) return false;
  if (storedRate == null || String(storedRate).trim() === "") return false;
  return Number.isFinite(Number.parseFloat(String(storedRate)));
}

// The document-level VAT rate a saved invoice was composed at.
//
// Stored since migration 151 (`storedRate`, server/document-vat-rate.js). An
// invoice stored before it has none unless the migration could tell what it
// was, and then, with per-line VAT off, every item's vat_rate is NULL and the
// rate survives only inside tax_amount, so re-opening it has to work it back
// out. Returns null when it genuinely can't be known (nothing taxable to
// divide by), which the compose screens read as "use the company default"
// rather than inventing a number.
//
// Order matters:
//  - Reverse charge first: tax_amount is 0 by definition there, and the rate
//    the document quotes lives in its own column. (So the 0 such an invoice
//    stores is never served to a compose screen.)
//  - Then explicit per-line rates. computeVatBands' callers freeze the
//    document rate onto every null line as soon as ONE line is explicit, so
//    these are all-or-nothing. A single shared value IS the document rate; a
//    genuine mix has no single answer, so take the largest — it's the one a
//    new line should default to, and the compose screen shows each line's own
//    rate anyway. Before the stored rate, because a document with rates on
//    its lines stores the rate its compose screen held for a new line (the
//    company's, with "Adjust VAT percentage" off), which is none of its
//    lines': an invoice whose every line is at 5% stores 20, and serving 20
//    seeded Make recurring and a custom credit at 20% (#970).
//  - Then the rate it stored, which is the rate it was composed at
//    (`servesStoredVatRate`).
//  - Otherwise divide the tax by what it was charged on.
export function documentVatRateFromTotals({
  subtotalPence = 0,
  discountPence = 0,
  taxPence = 0,
  reverseCharge = false,
  reverseChargeRate = null,
  lineRates = [],
  // A retention is held back before VAT (#932), so the tax was charged on
  // the value less it.
  retentionPence = 0,
  // The rate the invoice stored (migration 151), null for one that has none.
  storedRate = null
} = {}) {
  if (reverseCharge) {
    const rc = Number.parseFloat(String(reverseChargeRate ?? ""));
    return Number.isFinite(rc) ? rc : null;
  }

  const explicit = ownLineRates(lineRates);
  if (explicit.length) return Math.max(...explicit);

  if (servesStoredVatRate({ reverseCharge, lineRates, storedRate })) return Number.parseFloat(String(storedRate));

  const taxable = Math.round(subtotalPence) - Math.round(discountPence) - Math.round(Number(retentionPence) || 0);
  if (taxable <= 0) return null;
  // Two decimals, matching NUMERIC(5,2) — so a rate read back equals the rate
  // that was stored, and the select finds it among its known options.
  return Math.round((Math.round(taxPence) / taxable) * 10000) / 100;
}

// ---------------------------------------------------------------------------
// What to ask a client for right now, in pence.
//
// `nextAmountDue` above answers WHICH phase an invoice is waiting on; this
// answers HOW MUCH of it is left, which is a different question and was for a
// while a different implementation: `server/routes/stripe.js` worked it out
// inline for the Take payment screen, and the phone drawing the same screen
// offline would have been a second one (#619).
//
// The shape is the one `GET /invoices/:id/payment-status` already returns, so
// the route hands this straight back and the app reads the same fields whether
// the answer came over the wire or out of the bundle.
//
// **Null means "just ask for the balance."** A featured partial only makes
// sense when it is smaller than what is owed and large enough for the
// processor to take — otherwise the screen would offer two ways of saying the
// same number, or one the payment page would refuse.
// ---------------------------------------------------------------------------

export function nextAmountDuePence({
  instalments = [],
  // The three things that stand against a total, taken SEPARATELY and added
  // here. Callers used to hand in a settled figure they had summed
  // themselves — the route did, and the phone would have had to, which is a
  // second place adding up money in a codebase whose rule is that there are
  // none.
  paidPence = 0,
  creditedPence = 0,
  // Still accepted, so a caller that passes it is not wrong, but it no longer
  // counts towards a plan: each instalment's target already has its share of
  // the deduction taken out (`instalmentTargetPence`). Adding it here as well
  // put the whole deduction against the first stage before a penny arrived.
  cisDeductionPence = 0,
  // What credit notes took off the deduction (ADR-031, amended): a stage's
  // and a deposit's share is then of what still stands.
  cisCreditedPence = 0,
  depositAmountPence = 0,
  depositCisDeductionPence = 0,
  outstandingPence = 0,
  minimumPence = 0
} = {}) {
  const rows = instalmentsWithCisNow(Array.isArray(instalments) ? instalments : [], cisCreditedPence);
  const settledPence = (Number(paidPence) || 0) + (Number(creditedPence) || 0);
  let pence = 0;
  let featured = null;

  // A PLAN WINS, and an invoice cannot have both: a deposit and a payment
  // plan are mutually exclusive extras (`shared/invoice-extras.js`), so this
  // is a precedence nothing can actually exercise — it is written down so the
  // next reader does not have to work out whether it could.
  if (rows.length > 0) {
    const { nextDueSeq } = allocateInstalments(rows, settledPence);
    // Every instalment covered: there is nothing left to feature, and asking
    // for the balance is the only honest offer.
    if (nextDueSeq == null) return null;
    const ordered = [...rows].sort((a, b) => a.seq - b.seq);
    const target = ordered.find((row) => row.seq === nextDueSeq);
    // What has already landed on the instalments BEFORE this one does not
    // reduce it; what has spilled past them does.
    const earlierSum = ordered.reduce(
      (sum, row) => sum + (row.seq < nextDueSeq ? instalmentTargetPence(row) : 0),
      0
    );
    const size = instalmentTargetPence(target);
    pence = Math.max(0, size - Math.max(0, settledPence - earlierSum));
    featured = { kind: "instalment", seq: nextDueSeq, count: rows.length };
  } else {
    if (!((Number(depositAmountPence) || 0) > 0)) return null;
    // A deposit is met by CASH, matching the "deposit met" rule elsewhere —
    // a credit note does not pay one — less its share of any CIS deduction.
    const deposit = depositTargetPence({
      depositAmountPence,
      depositCisDeductionPence: depositCisNowPence({ depositCisDeductionPence, cisDeductionPence, cisCreditedPence })
    });
    pence = Math.max(0, deposit - (Number(paidPence) || 0));
    if (pence <= 0) return null;
    featured = { kind: "deposit", seq: null, count: null };
  }

  const capped = Math.min(pence, Number(outstandingPence) || 0);
  if (capped < (Number(minimumPence) || 0)) return null;
  if (capped >= (Number(outstandingPence) || 0)) return null;
  return featured.kind === "instalment"
    ? { kind: "instalment", pence: capped, seq: featured.seq, count: featured.count }
    : { kind: "deposit", pence: capped };
}

// ---------------------------------------------------------------------------
// VAT on a foreign-currency document, shown in sterling (#931,
// docs/designs/vat-in-sterling.md).
//
// A VAT-registered company can charge UK VAT on a document in another
// currency, and the law then asks for the VAT in sterling (VAT Regulations
// 1995, reg 14(1)(l)); HMRC's guidance also asks for the net at each rate.
// The document's own figures stay in its currency, and so does everything paid
// against it: the sterling figures are a disclosure, worked out from the
// document's VAT bands at one exchange rate, and never take part in settlement.
//
// THE RATE is how many units of the document's currency make £1, the way HMRC
// publishes its monthly rates (EUR 1.1665 means €1.1665 = £1), up to six
// decimal places. It is held as an integer number of millionths, so no float
// ever divides money: 1.1665 is 1166500.
// ---------------------------------------------------------------------------

export const EXCHANGE_RATE_DECIMALS = 6;
// NUMERIC(12,6) holds six whole digits. No supported currency is anywhere near.
const MAX_RATE_MICROS = 999999999999;

/**
 * A rate as typed or stored — "1.1665", 1.1665, "1.166500" — as millionths,
 * or null when it isn't a positive decimal with at most six places.
 */
export function exchangeRateMicros(value) {
  if (value == null) return null;
  const text = typeof value === "number"
    ? (Number.isFinite(value) ? value.toFixed(EXCHANGE_RATE_DECIMALS) : "")
    : String(value).trim();
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  // A number arrives through toFixed, so its extra zeros are ours; a typed
  // seventh place that isn't a zero is more precision than the column keeps.
  const trimmed = fraction.replace(/0+$/, "");
  if (trimmed.length > EXCHANGE_RATE_DECIMALS) return null;
  const micros = Number(whole) * 1e6 + Number(trimmed.padEnd(EXCHANGE_RATE_DECIMALS, "0") || 0);
  if (!Number.isSafeInteger(micros) || micros <= 0 || micros > MAX_RATE_MICROS) return null;
  return micros;
}

/** Millionths back to the shortest decimal that says them: 1166500 → "1.1665". */
export function exchangeRateText(micros) {
  const n = Number(micros);
  if (!Number.isSafeInteger(n) || n <= 0) return "";
  const whole = Math.floor(n / 1e6);
  const fraction = String(n % 1e6).padStart(EXCHANGE_RATE_DECIMALS, "0").replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : String(whole);
}

/**
 * An amount in the document's currency (in its minor units, which this
 * codebase calls pence whatever the currency) converted to sterling pence at
 * `rateMicros`, rounded half up to the penny. Exact: BigInt, never a float.
 */
export function toSterlingPence(pence, rateMicros) {
  const amount = Math.round(Number(pence) || 0);
  const rate = Number(rateMicros);
  if (!Number.isSafeInteger(rate) || rate <= 0) return 0;
  const negative = amount < 0;
  const n = BigInt(Math.abs(amount)) * BigInt(1000000);
  const d = BigInt(rate);
  const rounded = (n * BigInt(2) + d) / (d * BigInt(2));
  const result = Number(rounded);
  return negative ? -result : result;
}

/**
 * The sterling figures a document prints, from its VAT bands (computeVatBands)
 * and the rate: each band's net and VAT converted on its own, and the totals as
 * the sum of the bands, so the lines printed add up to the total printed.
 * Null when the rate isn't usable.
 */
export function sterlingVatFigures({ bands = [], rate }) {
  const rateMicros = exchangeRateMicros(rate);
  if (rateMicros == null) return null;
  const converted = (Array.isArray(bands) ? bands : []).map((band) => ({
    rate: band.rate,
    netPence: toSterlingPence(band.netPence, rateMicros),
    taxPence: toSterlingPence(band.taxPence, rateMicros)
  }));
  return {
    rate: Number(exchangeRateText(rateMicros)),
    bands: converted,
    netPence: converted.reduce((sum, band) => sum + band.netPence, 0),
    taxPence: converted.reduce((sum, band) => sum + band.taxPence, 0)
  };
}
